import { useMutation } from '@apollo/client';
import {
  InsertAssetDocument,
  namedOperations,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import type { InsertAssetTRPCInput } from './useInsertAssetTRPC';
import { useInsertAssetTRPC } from './useInsertAssetTRPC';

export const useInsertAsset = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [insertAssetGraphQL, graphqlState] = useMutation(InsertAssetDocument, {
    update: (cache) => {
      evictField(cache, 'asset');
      evictField(cache, 'asset_aggregate');
    },
    refetchQueries: [namedOperations.Query.getAssetById],
  });

  const trpcMutation = useInsertAssetTRPC();

  const insertAsset = async (
    variables: InsertAssetTRPCInput
  ): Promise<{ Id: string }> => {
    if (trpcEnabled) {
      return trpcMutation.insertAsset(variables);
    }

    const result = await insertAssetGraphQL({ variables });
    const Id = result.data?.insertChildAsset?.Id;
    if (!Id) {
      throw new Error('Failed to insert asset');
    }

    return { Id };
  };

  if (trpcEnabled) {
    return {
      insertAsset,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    insertAsset,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
